import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase } from "@/lib/supabase";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

interface AuctionItem {
  id: string;
  case_number: string;
  location: string;
  property_type: string;
  appraised_value: number;
  minimum_price: number;
  auction_date: string;
  auction_time: string;
  failure_count: number;
  grade: string;
  winning_bid: number;
  winning_bid_ratio: number;
  exclusive_area: number;
  rights_analysis: string;
}

const GRADE_COLORS: Record<string, string> = { A: "#10B981", B: "#F59E0B", C: "#9CA3AF" };
const REGION_COLORS = ["#C4956A", "#10B981", "#F59E0B", "#6366F1", "#EF4444"];

export default function AnalysisPage() {
  const [items, setItems] = useState<AuctionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await supabase
          .from("kangstudy_auction_items")
          .select("id,case_number,location,property_type,appraised_value,minimum_price,auction_date,auction_time,failure_count,grade,winning_bid,winning_bid_ratio,exclusive_area,rights_analysis")
          .order("auction_date", { ascending: true });
        setItems(
          (data || []).map((i: Record<string, unknown>) => ({
            id: i.id as string,
            case_number: (i.case_number as string) || "",
            location: (i.location as string) || "",
            property_type: (i.property_type as string) || "아파트",
            appraised_value: (i.appraised_value as number) || 0,
            minimum_price: (i.minimum_price as number) || 0,
            auction_date: (i.auction_date as string) || "",
            auction_time: (i.auction_time as string) || "",
            failure_count: (i.failure_count as number) || 0,
            grade: (i.grade as string) || "C",
            winning_bid: (i.winning_bid as number) || 0,
            winning_bid_ratio: (i.winning_bid_ratio as number) || 0,
            exclusive_area: (i.exclusive_area as number) || 0,
            rights_analysis: (i.rights_analysis as string) || "",
          }))
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const today = new Date();
  const total = items.length;
  const gradeA = items.filter((i) => i.grade === "A").length;
  const completed = items.filter((i) => i.winning_bid > 0);
  const upcoming = items.filter((i) => !i.winning_bid && i.auction_date >= today.toISOString().slice(0, 10)).slice(0, 5);

  const avgWinRatio =
    completed.length > 0
      ? Math.round(
          (completed.reduce((s, i) => {
            const r = i.winning_bid_ratio > 0 ? i.winning_bid_ratio : i.appraised_value > 0 ? (i.winning_bid / i.appraised_value) * 100 : 0;
            return s + r;
          }, 0) / completed.length) * 10
        ) / 10
      : 0;

  const gradeData = [
    { name: "A등급", value: items.filter((i) => i.grade === "A").length, color: "#10B981" },
    { name: "B등급", value: items.filter((i) => i.grade === "B").length, color: "#F59E0B" },
    { name: "C등급", value: items.filter((i) => i.grade === "C").length, color: "#9CA3AF" },
  ].filter((d) => d.value > 0);

  const regionMap: Record<string, number> = {};
  items.forEach((i) => {
    const r = i.location.split(" ").slice(0, 2).join(" ") || "기타";
    regionMap[r] = (regionMap[r] || 0) + 1;
  });
  const regionData = Object.entries(regionMap).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, value]) => ({ name, value }));

  const marketTrend = avgWinRatio >= 85 ? "과열" : avgWinRatio >= 75 ? "상승" : avgWinRatio >= 65 ? "보합" : "침체";
  const trendColor = avgWinRatio >= 85 ? "text-red-600" : avgWinRatio >= 75 ? "text-amber-600" : avgWinRatio >= 65 ? "text-emerald-600" : "text-[#8C8780]";

  return (
    <div className="min-h-screen bg-[#F5F3F0] font-['Noto_Sans_KR']">
      <Navbar />

      {/* 히어로 */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://img.echotwin.kr/kangstudy/d7b29d2b06f1bdb4.jpg')" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917]/80 via-[#1C1917]/70 to-[#1C1917]/80"></div>
        <div className="relative max-w-5xl mx-auto px-6 py-16 text-center">
          <span className="inline-block text-xs font-medium text-[#C4956A] bg-[#C4956A]/15 border border-[#C4956A]/30 px-3 py-1 rounded-full mb-4">
            강스터디 경매 시장 분석
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
            실제 데이터로 보는<br />법원 경매 시장 현황
          </h1>
          <p className="text-sm text-white/70 max-w-xl mx-auto mb-8">
            강스터디가 직접 분석한 경매 물건 데이터를 공개합니다.<br />
            등급별 분포, 지역별 현황, 낙찰가율 트렌드를 한눈에 확인하세요.
          </p>

          {/* 핵심 지표 3개 */}
          {!loading && total > 0 && (
            <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
              {[
                { label: "분석 물건", value: `${total}건`, icon: "ri-building-line" },
                { label: "A등급 물건", value: `${gradeA}건`, icon: "ri-star-line" },
                { label: "평균 낙찰가율", value: avgWinRatio > 0 ? `${avgWinRatio}%` : "집계 중", icon: "ri-percent-line" },
              ].map((s, i) => (
                <div key={i} className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-4 text-white">
                  <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center">
                    <i className={`${s.icon} text-xl text-[#C4956A]`}></i>
                  </div>
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-xs text-white/60 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-10">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#8C8780]">
            <i className="ri-loader-4-line animate-spin mr-2 text-xl"></i>
            <span>데이터 불러오는 중...</span>
          </div>
        ) : total === 0 ? (
          <div className="bg-white rounded-2xl border border-[#E8E5E0] p-12 text-center">
            <i className="ri-pie-chart-2-line text-4xl text-[#D8D3CC] mb-4"></i>
            <p className="text-sm font-medium text-[#5C5650]">아직 분석 데이터가 준비 중입니다</p>
            <p className="text-xs text-[#8C8780] mt-1">곧 업데이트됩니다. 블로그에서 최신 시장 분석을 먼저 확인해보세요.</p>
            <Link to="/blog" className="inline-block mt-4 px-5 py-2 text-xs font-medium bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all">
              블로그 보러가기
            </Link>
          </div>
        ) : (
          <>
            {/* 시장 지수 배너 */}
            <div className="bg-white rounded-2xl border border-[#E8E5E0] p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-[#8C8780] mb-1">강스터디 경매 시장 지수</p>
                  <div className="flex items-baseline gap-3">
                    <span className={`text-3xl font-bold ${trendColor}`}>{marketTrend}</span>
                    {avgWinRatio > 0 && (
                      <span className="text-sm text-[#8C8780]">낙찰가율 {avgWinRatio}% 기준</span>
                    )}
                  </div>
                  <p className="text-xs text-[#8C8780] mt-1">
                    {avgWinRatio >= 85 ? "경쟁이 매우 치열합니다. 입찰가 산정을 보수적으로 접근하세요." :
                     avgWinRatio >= 75 ? "상승 추세입니다. 선별적 투자가 효과적입니다." :
                     avgWinRatio >= 65 ? "안정적인 시장입니다. 등급별 선별 투자에 집중하세요." :
                     "침체 구간입니다. 권리분석이 완벽한 A등급 물건만 선별하세요."}
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "전체", value: `${total}건` },
                    { label: "A등급", value: `${gradeA}건`, color: "#10B981" },
                    { label: "낙찰완료", value: `${completed.length}건`, color: "#F59E0B" },
                    { label: "입찰예정", value: `${items.filter((i) => !i.winning_bid).length}건`, color: "#C4956A" },
                  ].map((s, i) => (
                    <div key={i} className="text-center bg-[#FAFAF8] rounded-lg px-3 py-2">
                      <p className="text-sm font-bold" style={{ color: s.color || "#1C1917" }}>{s.value}</p>
                      <p className="text-[10px] text-[#8C8780]">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 차트 row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* 등급 분포 */}
              <div className="bg-white rounded-2xl border border-[#E8E5E0] p-6">
                <h2 className="text-sm font-bold text-[#1C1917] mb-4">등급별 물건 분포</h2>
                {gradeData.length > 0 ? (
                  <div className="flex items-center gap-6">
                    <ResponsiveContainer width={120} height={120}>
                      <PieChart>
                        <Pie data={gradeData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" stroke="none">
                          {gradeData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2.5 flex-1">
                      {gradeData.map((g) => (
                        <div key={g.name}>
                          <div className="flex items-center justify-between mb-0.5">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }}></div>
                              <span className="text-xs text-[#5C5650]">{g.name}</span>
                            </div>
                            <span className="text-xs font-bold text-[#1C1917]">{g.value}건 ({Math.round((g.value / total) * 100)}%)</span>
                          </div>
                          <div className="h-1.5 bg-[#F0EDE8] rounded-full">
                            <div className="h-full rounded-full transition-all" style={{ width: `${(g.value / total) * 100}%`, backgroundColor: g.color }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-[#B8B3AE] text-center py-6">데이터 없음</p>
                )}
                <p className="text-xs text-[#B8B3AE] mt-4 border-t border-[#F0EDE8] pt-3">
                  A등급: 권리관계 깨끗, 투자 매력 높음 · B등급: 주의 필요 · C등급: 전문가 검토 필수
                </p>
              </div>

              {/* 지역별 분포 */}
              <div className="bg-white rounded-2xl border border-[#E8E5E0] p-6">
                <h2 className="text-sm font-bold text-[#1C1917] mb-4">지역별 물건 수 (상위 5)</h2>
                {regionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={regionData} layout="vertical" margin={{ left: 0, right: 16 }}>
                      <XAxis type="number" tick={{ fontSize: 10, fill: "#8C8780" }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#5C5650" }} axisLine={false} tickLine={false} width={75} />
                      <Tooltip formatter={(v) => [`${v}건`, "물건 수"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E8E5E0" }} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {regionData.map((_, i) => <Cell key={i} fill={REGION_COLORS[i % REGION_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-[#B8B3AE] text-center py-6">데이터 없음</p>
                )}
              </div>
            </div>

            {/* 이번 주 입찰 예정 물건 */}
            {upcoming.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#E8E5E0] p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-sm font-bold text-[#1C1917]">입찰 예정 물건 (최신 5건)</h2>
                    <p className="text-xs text-[#8C8780] mt-0.5">강스터디 등급 기준 선별</p>
                  </div>
                  <Link to="/auctions" className="text-xs text-[#C4956A] hover:underline font-medium whitespace-nowrap">
                    전체 보기 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="space-y-3">
                  {upcoming.map((item) => {
                    const discountPct = item.appraised_value > 0 ? Math.round((1 - item.minimum_price / item.appraised_value) * 100) : 0;
                    const daysLeft = Math.ceil((new Date(item.auction_date).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={item.id} className="flex items-center gap-4 p-3 rounded-xl border border-[#F0EDE8] hover:bg-[#FAFAF8] transition-colors">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm"
                          style={{ backgroundColor: (GRADE_COLORS[item.grade] || "#9CA3AF") + "20", color: GRADE_COLORS[item.grade] || "#9CA3AF" }}
                        >
                          {item.grade}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-[#1C1917] truncate">{item.location}</p>
                          <p className="text-[10px] text-[#8C8780] mt-0.5">
                            {item.property_type} · {item.auction_date}
                            {item.auction_time && ` ${item.auction_time}`}
                            {daysLeft <= 7 && <span className="ml-1.5 text-red-500 font-medium">D-{daysLeft}</span>}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs font-bold text-[#1C1917]">
                            {item.minimum_price > 0 ? `${Math.round(item.minimum_price / 10000).toLocaleString()}만` : "—"}
                          </p>
                          {discountPct > 0 && (
                            <p className="text-[10px] text-[#C4956A] font-medium">감정가 {discountPct}%↓</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 낙찰 결과 */}
            {completed.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#E8E5E0] p-6">
                <h2 className="text-sm font-bold text-[#1C1917] mb-5">최근 낙찰 결과 ({completed.length}건)</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[400px]">
                    <thead>
                      <tr className="border-b border-[#F0EDE8]">
                        <th className="pb-2 text-left font-semibold text-[#8C8780] pr-4">소재지</th>
                        <th className="pb-2 text-left font-semibold text-[#8C8780] pr-4">종류</th>
                        <th className="pb-2 text-right font-semibold text-[#8C8780] pr-4">감정가</th>
                        <th className="pb-2 text-right font-semibold text-[#8C8780] pr-4">낙찰가</th>
                        <th className="pb-2 text-center font-semibold text-[#8C8780]">낙찰가율</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completed.slice(0, 5).map((item) => {
                        const ratio = item.winning_bid_ratio > 0 ? item.winning_bid_ratio : item.appraised_value > 0 ? Math.round((item.winning_bid / item.appraised_value) * 1000) / 10 : 0;
                        return (
                          <tr key={item.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8]">
                            <td className="py-2 pr-4 text-[#5C5650] max-w-[130px] truncate">{item.location}</td>
                            <td className="py-2 pr-4"><span className="px-1.5 py-0.5 rounded bg-[#F5F3F0] text-[#5C5650]">{item.property_type}</span></td>
                            <td className="py-2 pr-4 text-right text-[#8C8780]">{item.appraised_value > 0 ? `${Math.round(item.appraised_value / 10000).toLocaleString()}만` : "—"}</td>
                            <td className="py-2 pr-4 text-right font-bold text-[#1C1917]">{Math.round(item.winning_bid / 10000).toLocaleString()}만</td>
                            <td className="py-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${ratio >= 90 ? "bg-red-100 text-red-600" : ratio >= 80 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}>
                                {ratio}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* CTA */}
        <div className="rounded-2xl overflow-hidden relative">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://img.echotwin.kr/kangstudy/f950004cb05a371b.jpg')" }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917]/90 to-[#1C1917]/60"></div>
          <div className="relative px-8 py-10 text-white">
            <p className="text-xs font-medium text-[#C4956A] mb-2">강스터디와 함께</p>
            <h3 className="text-xl font-bold mb-2">데이터 기반 경매 투자를 시작하세요</h3>
            <p className="text-sm text-white/70 mb-6 max-w-md">지금 공개된 데이터는 일부입니다. 강스터디 수강생은 등급별 전체 물건 분석, 권리분석 상세 정보, 입찰가 시뮬레이션을 모두 이용할 수 있습니다.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/courses" className="px-5 py-2.5 bg-[#C4956A] text-white text-sm font-semibold rounded-full hover:bg-[#B08050] transition-all whitespace-nowrap">
                강의 둘러보기 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
              <Link to="/bid-calculator" className="px-5 py-2.5 bg-white/15 border border-white/30 text-white text-sm font-medium rounded-full hover:bg-white/25 transition-all whitespace-nowrap">
                입찰계산기 사용하기
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}